import moment from 'moment';

const computeCart = (state) => {
    const payload = {
        pricingVersion: state.pricingVersion,
        pricingVersionRevision: state.pricingVersionRevision,
        context: state.context,
        pricingPlanId: state.plan,
        vatIdentificationNumber: state.vatIntra,
        items: _.map(state.products, (product, productId) => {
            /**
             * We must sort array by amount for back process to keep pricing in order
             * Free licences must be the first item
             * Max price licences must be last item to get up_to at 'inf'
             */
            const productsWithQuantity = product.customPricing.filter(price => price.qt > 0).sort((previous, next) => previous.unit_amount - next.unit_amount);
            return {
                product: productId,
                quantity: product.quantity,
                customPricing: productsWithQuantity.map((price, index) => {
                    const isFirst = index === 0;
                    const isLast = index === productsWithQuantity.length - 1;
                    const quantityInRange = productsWithQuantity.slice(0, index).map(item => item.qt).reduce((acc, value) => acc + value, 0);
                    return {
                        up_to: isLast ? 'inf' : isFirst ? price.qt : quantityInRange + price.qt,
                        unit_amount: price.unit_amount
                    };
                })
            }
        }).filter(product => product.quantity > 0),
        paymentMethod: state.paymentMethod,
    };
    if(!_.isNil(state.billingSettings.countryCode)) payload.country = state.billingSettings.countryCode;

    return payload;
}

/**
 * 
 * @param {*} state 
 * @returns {} data of cart
 */
const persistCart = (state) => {
    let products = {};

    _.each(state.products, (product, productId) => {
        if(product.quantity === 0) return;
        const productsWithQuantity = product.customPricing.filter(price => price.qt > 0).sort((previous, next) => previous.unit_amount - next.unit_amount);

        products = {
            ...products,
            [productId]: {
                quantity: product.quantity,
                customPricing: productsWithQuantity.map((price, index) => {
                    const isFirst = index === 0;
                    const isLast = index === productsWithQuantity.length - 1;
                    const quantityInRange = productsWithQuantity.slice(0, index).map(item => item.qt).reduce((acc, value) => acc + value, 0);
                    return {
                        up_to: isLast ? 'inf' : isFirst ? price.qt : quantityInRange + price.qt,
                        unit_amount: price.unit_amount,
                        qt: price.qt
                    };
                })
            }
        }
    });

    const context = state.context === 'renew' && (!!state.previousPricingVersion && state.previousPricingVersion < 3) ? 'legacy_renew' : state.context;

    return {
        customer: state.customerId,
        billingSettings: {
            contactName: state.contact.name,
            contactFirstname: state.contact.forename,
            contactEmail: state.contact.email,
            contactPhone: state.contact.phone.e164,
            locale: state.contact.billingLocale,
            vatIdentificationNumber: state.vatIntra,
        },
        billingAddress: {
            line1: state.billingSettings.line1,
            line2: state.billingSettings.line2 || '',
            postalCode: state.billingSettings.postalCode,
            state: state.billingSettings.state,
            city: state.billingSettings.city,
            country: state.billingSettings.countryCode,
        },
        cart : {
            context: context,
            previousPlanCode: state.previousPlanCode || null,
            pricingVersion: state.pricingVersion,
            pricingVersionRevision: state.pricingVersionRevision,
            plan: state.plan,
            products,
            paymentMethod: state.paymentMethod,
            source: state.source,
            activationDate: _.isNil(state.periodApplianceStart) || !state.periodApplianceStart.length ? null : moment(state.periodApplianceStart).format('YYYY-MM-DD HH:mm:ss'),
            expirationDate: _.isNil(state.expirationDate) || !state.expirationDate.length ? null : moment(state.expirationDate).format('YYYY-MM-DD HH:mm:ss'),
            billingContactPayer: state.billingContactPayer,
            billingContactPayerEmail: state.billingContactPayerEmail,
            message : state.mailCustomization.message,
            messageUsed : state.mailCustomization.messageUsed,
            earlyRenewal: state.earlyRenewal
        }
    }
}

const generateQuantity = (product, isClient = false) => {
    return {
        ...product,
        customPricing: product.customPricing.map((price, index) => {
            let qt;
            let min;
            const isFirstPrice = index === 0;
            const isLastPrice = index === product.customPricing.length - 1;

            if(isFirstPrice) {
                qt = price.up_to === 'inf' ? product.quantity : price.up_to;
            } else if (isLastPrice) {
                qt = product.quantity - product.customPricing[index - 1].up_to;
            } else {
                qt = price.up_to - product.customPricing[index - 1].up_to;
            }

            /** Higher level get addition of his quantity and free quantity
            * But other keep their own quantity
            */
            if(isClient) {
                min = product.customPricing.length === 1 ? product.quantity : qt;

                const freeRank = product.customPricing.find(cp => cp.unit_amount === 0);
                if(price.up_to === 'inf' && !!freeRank)  {
                    min += freeRank.up_to;
                }
            } else {
                min = qt;
            }
            return {
                ...price,
                qt,
                min
            }
        })
    }
}

const errorsLabels = {
    // CREDIT CARD
    invalid_number: window.i18n.__("Le numéro de carte n'est pas un numéro de carte de crédit valide."),
    invalid_expiry_month: window.i18n.__("Le mois d'expiration de la carte n'est pas valide"),
    invalid_expiry_year: window.i18n.__("L'année d'expiration de la carte n'est pas valable"),
    invalid_cvc: window.i18n.__("Le code de sécurité de la carte n'est pas valide"),
    incorrect_number: window.i18n.__("Le numéro de carte est incorrect"),
    incomplete_number: window.i18n.__("Le numéro de carte est incomplet"),
    incomplete_cvc: window.i18n.__("Le code de sécurité de la carte est incomplet"),
    incomplete_expiry: window.i18n.__("La date d'expiration de la carte est incomplète"),
    expired_card: window.i18n.__("La carte a expiré"),
    incorrect_cvc: window.i18n.__("Le code de sécurité de la carte est incorrect"),
    incorrect_zip: window.i18n.__("Le code postal de la carte n'a pas été validé."),
    invalid_expiry_year_past: window.i18n.__("L'année d'expiration de la carte est dans le passé"),
    card_declined: window.i18n.__("La carte a été refusée"),
    missing: window.i18n.__("Il n'y a pas de carte sur le compte d'un client qui fait l'objet d'un débit."),
    processing_error: window.i18n.__("Une erreur s'est produite lors du traitement de la carte"),
    // PAYMENT INTENT
    payment_intent_action_required: window.i18n.__("Actions requises pour compléter le paiement"),
    payment_intent_authentication_failure: window.i18n.__("L'authentification pour le moyen de paiement a échouée"),
    payment_intent_incompatible_payment_method: window.i18n.__("Le moyen de paiement est incompatible"),
    payment_intent_invalid_parameter: window.i18n.__("Paiement non autorisé"),
    payment_intent_payment_attempt_failed: window.i18n.__("Échec lors du paiement"),
    payment_intent_unexpected_state: window.i18n.__("Erreur sur le statut du paiement"),
    // error code custom for being more specific
    payment_intent_unexpected_state_succeeded: window.i18n.__("Le paiement a déjà été enregistré"),
    card_declined_insufficient_funds: window.i18n.__("La carte ne dispose pas de fonds suffisants."),
};

const getErrorLabel = (code) => {
    return !_.isNil(errorsLabels[code]) ? errorsLabels[code] : window.i18n.__("Une erreur s'est produite");
}

const getErrorInput = (code) => {
    switch (code) {
        case 'invalid_expiry_month':
        case 'invalid_expiry_year':
        case 'incomplete_expiry':
        case 'invalid_expiry_year_past':
        case 'expired_card':
            return 'cardExpiry';
        case 'invalid_cvc':
        case 'incomplete_cvc':
        case 'incorrect_cvc':
            return 'cardCvc';
        case 'invalid_number':
        case 'incorrect_number':
        case 'incomplete_number':
        case 'card_declined':
            return 'cardNumber';
    }
}

const fetchCart = (id, params) => {
    return new Promise((resolve, reject) => {
        SellsyJS.WebServiceCaller.get(`/rest/sellsy-account-manager/cart/${id}`, params, (response) => {
            resolve(response);
        }, err => {
            reject(err);
        })
    })
};

const fetchMetadata = (customerId, context, terms) => {
    return new Promise((resolve, reject) => {
        SellsyJS.WebServiceCaller.get(`/rest/sellsy-account-manager/customer/${customerId}/cart-metadata`, { context: context, terms_type: terms, pricingversion: 4 }, (res) => {
            resolve(res);
        }, err => {
            reject(err);
        })
    })
};

const findPackWithSameGroupIn = (findable, packToFind) => {
    return findable.find(licenced => licenced.groups.some(groupLicenced => packToFind.groups.includes(groupLicenced)));
};

export {
    persistCart,
    computeCart,
    generateQuantity,
    fetchCart,
    fetchMetadata,
    getErrorLabel,
    getErrorInput,
    findPackWithSameGroupIn,
}